import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import Loading from '@/components/Loading';

import CopyToClipboard from '@/components/UI/CopyToClipboard';
import Icon from '@/components/UI/Icon';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList from '@/components/UI/SelectDropDown/Menus/ActionList';
import Tooltip from '@/components/UI/Tooltip';
import Truncate from '@/components/UI/Truncate/Truncate';
import { errorToast, successToast, trunc } from '@/helpers';
import { _isEmpty, _isNil, _isNotEmpty, _notNil, uuid } from '@/littledash';
import type { ID } from '@/model/Common.model';
import cn from 'classnames';
import { type MetadataFieldTypes } from '@/model/Metadata.model';
import { type ChangeEvent, type FC, type FocusEvent, Fragment, useCallback, useState } from 'react';
import { RiInboxArchiveLine, RiInboxUnarchiveFill } from 'react-icons/ri';
import { useTableEditState } from './useTableEditState';
import { RequiredCheckboxCell } from './RequiredCheckboxCell';
import { ExpandableIcon } from './ExpandableIcon';
import Options from './Options';
import { IndividualMetadataTitle } from './IndividualMetadataTitle';
import './IndividualSaveRepeater.scss';
import {
  createSingleMetadataItem,
  decodeOptionsForDisplay,
  type Errors,
  type ExtendedMetadataItem,
  sortActiveFirstThenAlphabetical,
  toggleArchiveMetadataItem,
  updateSingleMetadataItem,
} from './IndividualSaveRepeater.utils';
import {
  METADATA_OPTION_MAX_LENGTH,
  METADATA_TITLE_MAX_LENGTH,
  TABLE_CLASSES,
} from './IndividualSaveRepeater.constants';
import {
  type AddCategoryProps,
  type ArchiveButtonProps,
  type IndividualSaveRepeaterProps,
  type MetadataGlossaryFieldType,
  selectableFields,
} from './IndividualSaveRepeater.model';
import EntitySchemaTable from './EntitySchemaTable';

export const ToggleArchiveButton: FC<ArchiveButtonProps> = ({ row, handleArchive, disabled = false }) => (
  <Button soft onClick={() => handleArchive(row.id)} className="ml3 action-button" disabled={disabled}>
    {(row.active ?? true) ? (
      <Tooltip render="Archive">
        <RiInboxArchiveLine size={22} className="dark-gray" />
      </Tooltip>
    ) : (
      <Tooltip render="Unarchive">
        <RiInboxUnarchiveFill size={22} className="black hover-near-black" />
      </Tooltip>
    )}
  </Button>
);

const IndividualSaveRepeater: FC<IndividualSaveRepeaterProps> = ({
  data = [],
  setData,
  errors = [],
  type,
  title,
  initialDataLookup = {},
  onRefreshGlobalData,
  hasLoaded,
  hasError,
  setFormErrors,
}) => {
  const selectFields: MetadataFieldTypes[] = ['select', 'multi_select'];
  const [savingItems, setSavingItems] = useState<Set<ID>>(new Set());
  const [showArchivedItems, setShowArchivedItems] = useState<boolean>(false);
  const { isEditing, startEdit, cancelEdit } = useTableEditState<ExtendedMetadataItem>();
  const [localValues, setLocalValues] = useState<Record<string, any>>({});
  const [localUIState, setLocalUIState] = useState<Record<string, { expanded?: boolean }>>({});
  const [currentOptionsState, setCurrentOptionsState] = useState<Record<string, string[]>>({});

  const initialItem = {
    index: (_isNotEmpty(data) ? data[data.length - 1]?.index : -1) + 1,
    title: '',
    type,
    field_type: 'select' as MetadataFieldTypes,
    options: [''],
    active: true,
  };

  const getFieldValue = useCallback(
    (itemId: string | number | undefined, fieldName: string) => {
      if (_isNil(itemId)) {
        return '';
      }
      const localKey = `${itemId}_${fieldName}`;

      if (localKey in localValues) {
        return localValues[localKey];
      }

      const item = data.find((d) => d?.id && String(d.id) === String(itemId));
      const value = item && fieldName in item ? ((item as unknown as Record<string, unknown>)[fieldName] ?? '') : '';

      return value;
    },
    [localValues, data]
  );

  const getExpandedState = useCallback(
    (itemId: string | number | undefined) => {
      if (_isNil(itemId)) {
        return false;
      }

      if (itemId in localUIState) {
        return localUIState[itemId].expanded ?? false;
      }

      const item = data.find((d) => d?.id && String(d.id) === String(itemId));
      return item?.expanded ?? false;
    },
    [localUIState, data]
  );

  const clearLocalValue = useCallback(
    (itemId: string | number | undefined, fieldName?: string) => {
      if (!itemId) {
        return;
      }

      setLocalValues((prev) => {
        const newValues = { ...prev };
        if (fieldName) {
          delete newValues[`${itemId}_${fieldName}`];
        } else {
          Object.keys(newValues).forEach((key) => {
            if (key.startsWith(`${itemId}_`)) {
              delete newValues[key];
            }
          });
        }
        return newValues;
      });

      setLocalUIState((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });

      const item = data.find((d) => d?.id && String(d.id) === String(itemId));
      if (_notNil(item)) {
        const clearOptionsFunction = (item as unknown as Record<string, unknown>)._clearOptions;
        if (typeof clearOptionsFunction === 'function') {
          clearOptionsFunction();
        }
      }
    },
    [data]
  );

  const checkDuplicateTitle = useCallback(
    (itemToCheck: ExtendedMetadataItem): boolean => {
      if (_isNil(itemToCheck?.id)) {
        return false;
      }

      const currentTitle = getFieldValue(itemToCheck.id, 'title');
      if (_isNil(currentTitle) || _isEmpty(String(currentTitle).trim())) {
        return false;
      }

      const normalizedTitle = String(currentTitle).trim().toLowerCase();
      const filteredData = data.filter((d) => _notNil(d?.type) && d.type === type);

      const duplicateFound = filteredData.some((otherItem) => {
        if (_isNil(otherItem?.id) || String(otherItem.id) === String(itemToCheck.id)) {
          return false;
        }

        const otherTitle = getFieldValue(otherItem.id, 'title');
        if (_isNil(otherTitle)) {
          return false;
        }

        const otherNormalizedTitle = String(otherTitle).trim().toLowerCase();
        return normalizedTitle === otherNormalizedTitle && otherItem.type === itemToCheck.type;
      });

      return duplicateFound;
    },
    [data, type, getFieldValue]
  );

  const validateDuplicateTitle = (item: ExtendedMetadataItem): boolean => {
    try {
      // Skip validation for project_meta type
      if (item?.type === 'project_meta') {
        return true;
      }

      if (_isNil(item?.title) || _isEmpty(String(item?.title ?? '').trim())) {
        return true;
      }

      const itemId = item?.id;
      if (_isNil(itemId)) {
        return true;
      }

      if (checkDuplicateTitle(item)) {
        const typeDisplayName = (item?.type ?? '').replace('_', ' ') || 'this type';
        errorToast(`A field with the title "${item?.title ?? ''}" already exists for ${typeDisplayName}`);
        return false;
      }

      return true;
    } catch (error) {
      errorToast('Error validating field title. Please try again.');
      return true;
    }
  };

  const cancelItemEdit = useCallback(
    (itemId: ID) => {
      cancelEdit(itemId);
      clearLocalValue(itemId);
    },
    [cancelEdit, clearLocalValue]
  );

  const isSaveDisabled = (item: ExtendedMetadataItem): boolean => {
    try {
      const itemId = item?.id;
      if (_isNil(itemId)) {
        return true;
      }

      const currentTitle = getFieldValue(itemId, 'title');
      const trimmedTitle = String(currentTitle ?? '').trim();

      if (_isNil(currentTitle) || _isEmpty(trimmedTitle)) {
        return true;
      }

      if (trimmedTitle.length > METADATA_TITLE_MAX_LENGTH) {
        return true;
      }

      if (checkDuplicateTitle(item)) {
        return true;
      }

      if (_notNil(item.field_type) && selectFields.includes(item.field_type as MetadataFieldTypes)) {
        const currentOptions = currentOptionsState[itemId] || item.options;

        if (_notNil(currentOptions) && Array.isArray(currentOptions)) {
          const hasBlankOptions = currentOptions.some((option) => String(option ?? '').trim() === '');
          if (hasBlankOptions) {
            return true;
          }

          const hasMaxLengthExceeded = currentOptions.some(
            (option) => String(option ?? '').length > METADATA_OPTION_MAX_LENGTH
          );
          if (hasMaxLengthExceeded) {
            return true;
          }

          const normalizedOptions = currentOptions
            .map((option) =>
              String(option ?? '')
                .trim()
                .toLowerCase()
            )
            .filter((option) => option !== '');

          const uniqueOptions = new Set(normalizedOptions);
          if (normalizedOptions.length !== uniqueOptions.size) {
            return true;
          }
        }
      }

      return false;
    } catch (error) {
      return true;
    }
  };

  const createDuplicateErrors = useCallback((metas: Array<ExtendedMetadataItem>) => {
    const duplicateErrors: Array<Errors> = [];

    metas.forEach((item) => {
      duplicateErrors.push({
        id: item.id ?? `temp-${item.index}`,
        index: item.index,
        type: item.type ?? '',
        errors: {
          title: ['Category must be unique.'],
          options: [],
        },
      });
    });

    return duplicateErrors;
  }, []);

  const saveItemIndividually = async (itemId: ID) => {
    const item = data.find((d) => String(d?.id) === String(itemId));
    if (!item) {
      return;
    }

    // Get local options directly instead of relying on async state update
    const getLocalOptionsFunction = (item as unknown as Record<string, unknown>)._getLocalOptions;
    const currentOptions = typeof getLocalOptionsFunction === 'function' ? getLocalOptionsFunction() : item.options;

    const itemWithLocalValues = { ...item, options: currentOptions };
    Object.keys(localValues).forEach((key) => {
      if (key.startsWith(`${itemId}_`)) {
        const fieldName = key.substring(`${itemId}_`.length);
        (itemWithLocalValues as unknown as Record<string, unknown>)[fieldName] = localValues[key];
      }
    });

    if (itemId in localUIState) {
      Object.assign(itemWithLocalValues, localUIState[itemId]);
    }

    if (!validateDuplicateTitle(itemWithLocalValues)) {
      return;
    }

    setSavingItems((prev) => new Set([...prev, itemId]));

    try {
      const response = itemWithLocalValues.new
        ? await createSingleMetadataItem(itemWithLocalValues)
        : await updateSingleMetadataItem(itemWithLocalValues);

      const freshExtendedItem: ExtendedMetadataItem = decodeOptionsForDisplay({
        ...itemWithLocalValues,
        ...response,
        expanded: itemWithLocalValues?.expanded,
        new: false,
      });

      // Update data to reflect the saved state
      const updatedData = data.map((dataItem: ExtendedMetadataItem) => {
        if (String(dataItem?.id) === String(itemId)) {
          return freshExtendedItem;
        }
        return dataItem;
      });

      if (itemWithLocalValues.new) {
        const savedItem = updatedData.find((d) => String(d?.id) === String(itemId));
        const otherItems = updatedData.filter((d) => String(d?.id) !== String(itemId));

        const allItems = _notNil(savedItem) ? [...otherItems, savedItem] : otherItems;
        setData(
          sortActiveFirstThenAlphabetical(allItems.filter((item): item is ExtendedMetadataItem => _notNil(item)))
        );
      } else {
        setData(updatedData);
      }

      const keyForLookup = _notNil(freshExtendedItem?.api_id)
        ? String(freshExtendedItem.api_id)
        : String(freshExtendedItem?.id);

      if (_notNil(keyForLookup)) {
        initialDataLookup[keyForLookup] = { ...freshExtendedItem };
      }

      successToast(`${freshExtendedItem?.title ?? 'Item'} saved successfully`);

      cancelItemEdit(itemId);

      await onRefreshGlobalData?.();
    } catch (error) {
      const err = error as { response?: { status?: number; data?: { errors?: { title?: Array<string> } } } };
      const fieldTitle = itemWithLocalValues?.title ?? 'item';
      if (
        err.response?.status === 400 &&
        err.response?.data?.errors?.title?.some((msg) => msg.includes('Duplicate title.'))
      ) {
        setFormErrors(createDuplicateErrors([itemWithLocalValues]));
        errorToast(`A field with the title "${fieldTitle}" already exists`);
      } else {
        errorToast(`Failed to save ${fieldTitle}`);
      }
    } finally {
      setSavingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const handleEditClick = (itemId: ID) => {
    const item = data.find((d) => d.id === itemId);

    const isCurrentlyEditing = isEditing(itemId);

    if (isCurrentlyEditing) {
      cancelEdit(itemId);
    } else if (_notNil(item)) {
      startEdit(itemId, item);
    }

    if (_notNil(item) && _notNil(item.field_type) && selectFields.includes(item.field_type as MetadataFieldTypes)) {
      if (isCurrentlyEditing) {
        toggleExpanded(true, itemId);
      } else {
        toggleExpanded(false, itemId);
      }
    }
  };

  const handleCancelEdit = (itemId: ID) => {
    const item = data.find((d) => d?.id === itemId);

    if (_notNil(item) && item.new) {
      setData([...data.filter((item: ExtendedMetadataItem) => String(item?.id) !== String(itemId))]);
      cancelItemEdit(itemId);
      return;
    }

    if (_notNil(item)) {
      const lookupKey = _notNil(item?.api_id) ? String(item.api_id) : String(item?.id);
      const originalItem = initialDataLookup[lookupKey];

      if (_notNil(originalItem)) {
        setData([
          ...data.map((dataItem: ExtendedMetadataItem) => {
            if (String(dataItem?.id) === String(itemId)) {
              const shouldCollapse =
                _notNil(item?.field_type) && selectFields.includes(item.field_type as MetadataFieldTypes);
              const restoredItem = {
                ...originalItem,
                expanded: shouldCollapse ? false : item?.expanded,
              };
              return restoredItem;
            }
            return dataItem;
          }),
        ]);
        cancelItemEdit(itemId);
      } else {
        errorToast('Unable to cancel changes - original data not found');
      }
    }
  };

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const metadataId = event.target.getAttribute('data-id');
    const fieldName = event.target.name;
    const fieldValue = event.target.value;

    if (!metadataId) {
      return;
    }

    // Update local state only, don't sync to global state until saved
    setLocalValues((prev) => ({
      ...prev,
      [`${metadataId}_${fieldName}`]: fieldValue,
    }));
  }, []);

  const handleTitleBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    const metadataId = event.target.getAttribute('data-id');
    if (_isNil(metadataId)) {
      return;
    }

    const trimmedTitle = String(event.target.value ?? '').trim();
    setLocalValues((prev) => ({
      ...prev,
      [`${metadataId}_title`]: trimmedTitle,
    }));
  }, []);

  const toggleExpanded = useCallback((expanded: boolean, id?: ID) => {
    if (!id) {
      return;
    }

    // Update local UI state only, don't sync to global state until saved
    setLocalUIState((prev) => ({
      ...prev,
      [id]: { expanded: !expanded },
    }));
  }, []);

  const notifyOptionsChanged = useCallback((itemId: ID, currentOptions: string[]) => {
    setCurrentOptionsState((prev) => ({
      ...prev,
      [itemId]: currentOptions,
    }));
  }, []);

  const handleAdd = async (field_type: MetadataGlossaryFieldType) => {
    const newItem = {
      ...initialItem,
      id: uuid(),
      field_type,
      new: true,
      expanded: selectFields.includes(field_type as MetadataFieldTypes),
    };

    setData([...data, newItem]);

    startEdit(newItem.id, newItem);
  };

  const handleArchive = async (id?: ID) => {
    if (_isNil(id)) {
      return;
    }
    const item = data.find((item: ExtendedMetadataItem) => String(item?.id) === String(id));
    if (_isNil(item)) {
      return;
    }

    setSavingItems((prev) => new Set([...prev, id]));

    try {
      const updatedItem = await toggleArchiveMetadataItem(item);
      successToast(`${item?.title ?? 'Item'} ${(item?.active ?? true) ? 'archived' : 'unarchived'} successfully`);

      cancelItemEdit(id);

      const freshExtendedItem: ExtendedMetadataItem = decodeOptionsForDisplay({
        ...item,
        ...updatedItem,
        expanded: item?.expanded,
      });

      setData(
        sortActiveFirstThenAlphabetical([
          ...data.map((dataItem: ExtendedMetadataItem) => {
            const dataApiId = dataItem?.api_id;
            const updatedApiId = (updatedItem as ExtendedMetadataItem)?.api_id;
            return (dataApiId as string) === (updatedApiId as string) ? freshExtendedItem : dataItem;
          }),
        ])
      );

      if (_notNil(freshExtendedItem?.api_id)) {
        initialDataLookup[freshExtendedItem.api_id] = { ...freshExtendedItem };
      }
      if (_notNil(freshExtendedItem?.id)) {
        initialDataLookup[String(freshExtendedItem.id)] = { ...freshExtendedItem };
      }

      await onRefreshGlobalData?.();
    } catch (error) {
      errorToast(`Failed to ${(item?.active ?? true) ? 'archive' : 'unarchive'} ${item?.title ?? 'item'}`);
    } finally {
      setSavingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  if (!hasLoaded && !hasError) {
    return <Loading txt={`Loading ${title} metadata...`} />;
  }

  const filteredData = data.filter((d) => d?.type === type);
  const activeItems = filteredData.filter((item) => (item?.active ?? true) !== false);
  const archivedItems = filteredData.filter((item) => (item?.active ?? true) === false);
  const displayedItems = showArchivedItems ? filteredData : activeItems;

  return (
    <div className="pb7">
      {type === 'subject_meta' && <EntitySchemaTable />}
      <h3 className="f5 normal basier-med lh-title mb1 mt4" data-testid="metadata-heading">
        Custom fields
      </h3>
      <p className="f6 mt1" data-testid="metadata-description">
        These fields can be configured, edited or archived.
      </p>
      {_isNotEmpty(filteredData) ? (
        <>
          <div className="ui-card overflow-hidden ui__table ui__repeater bb b--moon-gray mt4">
            <table className="w-100 tl bt b--moon-gray individual-save-table" data-testid="metadata-table">
              <thead className="bg-near-white">
                <tr>
                  <th className={TABLE_CLASSES.TH}></th>
                  <th className={TABLE_CLASSES.TH} data-testid="field-name-header">
                    Field name
                  </th>
                  <th className={TABLE_CLASSES.TH} data-testid="field-type-header">
                    Field type
                  </th>
                  <th className={TABLE_CLASSES.TH} data-testid="slug-header">
                    Slug
                  </th>
                  {type === 'sample_meta' && (
                    <th className={TABLE_CLASSES.TH} data-testid="required-header">
                      Required
                    </th>
                  )}
                  <th className={TABLE_CLASSES.TH}></th>
                </tr>
              </thead>
              <tbody className="black">
                {_isEmpty(displayedItems) && (
                  <tr className="bg-light-gray empty-state-row">
                    <td className={TABLE_CLASSES.TD}></td>
                    <td className={TABLE_CLASSES.TD} colSpan={type === 'sample_meta' ? 5 : 4}>
                      <div className="flex flex-column white min-h5 justify-center items-center tc pa3 ma3">
                        <h2 className="tc pa3 ma3">No metadata has been added for {title}.</h2>
                      </div>
                    </td>
                    <td className={TABLE_CLASSES.TD}></td>
                  </tr>
                )}
                {displayedItems.map((row) => {
                  const isSaving = _notNil(row.id) ? savingItems.has(row.id) : false;
                  const isCurrentlyEditing = _notNil(row.id) ? isEditing(row.id) : false;
                  const hasDuplicateTitle =
                    isCurrentlyEditing === true && _notNil(row.id) ? checkDuplicateTitle(row) : false;
                  const hasTitleTooLong =
                    isCurrentlyEditing === true && _notNil(row.id)
                      ? String(getFieldValue(row.id, 'title') ?? '').trim().length > METADATA_TITLE_MAX_LENGTH
                      : false;

                  return (
                    <Fragment key={row.id ?? row.index}>
                      <tr
                        className={cn({
                          'o-50': isSaving === true,
                          'o-60 bg-light-gray': (row.active ?? true) === false,
                        })}
                      >
                        <td className={TABLE_CLASSES.TD}>
                          <ExpandableIcon
                            itemId={row.id}
                            isExpandable={
                              _notNil(row.field_type) && selectFields.includes(row.field_type as MetadataFieldTypes)
                            }
                            isExpanded={getExpandedState(row.id)}
                            onToggle={(itemId) => toggleExpanded(getExpandedState(itemId), itemId)}
                            testIdPrefix={row.slug ?? 'field'}
                          />
                        </td>

                        <td className={cn(TABLE_CLASSES.TD, 'field-name-cell')}>
                          {!row.id || !isEditing(row.id) ? (
                            <div
                              className={cn('f7 f6-l', {
                                'dark-gray': !(row.active ?? true),
                              })}
                              data-testid={`field-title-${row.slug ?? 'untitled'}`}
                            >
                              <Truncate tooltip={String(getFieldValue(row.id, 'title') || 'Untitled')}>
                                {String(getFieldValue(row.id, 'title') || 'Untitled')}
                              </Truncate>
                            </div>
                          ) : (
                            <IndividualMetadataTitle
                              title={getFieldValue(row.id, 'title')}
                              errors={errors}
                              id={row.id}
                              handleChange={handleChange}
                              onBlur={handleTitleBlur}
                              disabled={!(row.active ?? true) || isSaving}
                              hasDuplicate={hasDuplicateTitle}
                            />
                          )}
                        </td>
                        <td className={TABLE_CLASSES.TD}>
                          <span className={!(row.active ?? true) ? 'dark-gray' : ''}>{row.field_type}</span>
                        </td>
                        <td className={TABLE_CLASSES.TD}>
                          {_notNil(row.slug) && (
                            <CopyToClipboard textToCopy={row.slug} toastText="Slug copied to clipboard">
                              <span className={!(row.active ?? true) ? 'dark-gray' : ''}>{trunc(row.slug, 20)}</span>
                            </CopyToClipboard>
                          )}
                        </td>
                        {type === 'sample_meta' && (
                          <td className={TABLE_CLASSES.TD}>
                            <RequiredCheckboxCell
                              itemId={row.id}
                              isEditing={row.id ? isEditing(row.id) : false}
                              isEditable={true}
                              required={row.required ?? false}
                              isSaving={isSaving}
                              isActive={row.active ?? true}
                              onChange={(itemId, checked) => {
                                const updatedData = data.map((item: ExtendedMetadataItem) => {
                                  if (String(item?.id) === String(itemId)) {
                                    return { ...item, required: checked };
                                  }
                                  return item;
                                });
                                setData(updatedData);
                              }}
                              testIdPrefix={row.slug ?? 'field'}
                            />
                          </td>
                        )}
                        <td className={cn(TABLE_CLASSES.TD, 'v-mid')}>
                          <div className="flex items-center justify-end">
                            {row.new ? (
                              <div className="spacer-40"></div>
                            ) : (
                              <ToggleArchiveButton row={row} handleArchive={handleArchive} disabled={isSaving} />
                            )}
                            {row.id && !isEditing(row.id) && (row.active ?? true) && (
                              <>
                                <div
                                  className="ml3 pointer flex items-center"
                                  onClick={() => row.id && handleEditClick(row.id)}
                                  data-testid={`edit-button-${row.slug ?? 'field'}`}
                                >
                                  <Tooltip render="Edit field">
                                    <Icon icon="pencil" width="24" height="24" className="hover-near-black dark-gray" />
                                  </Tooltip>
                                </div>
                                <div className="ml3 spacer-2"></div>
                              </>
                            )}
                            {row.id && !isEditing(row.id) && !(row.active ?? true) && (
                              <>
                                <div className="spacer-82"></div>
                              </>
                            )}
                            {row.id && isEditing(row.id) && (
                              <>
                                <div
                                  className="ml3 pointer flex items-center"
                                  onClick={() => row.id && handleCancelEdit(row.id)}
                                  data-testid={`cancel-button-${row.slug ?? 'field'}`}
                                >
                                  <Tooltip render="Cancel changes">
                                    <Icon icon="close" width="12" height="12" className="dark-gray hover-red" />
                                  </Tooltip>
                                </div>
                                <div
                                  className={cn('ml3 flex items-center', {
                                    'o-50': isSaveDisabled(row) || isSaving,
                                    pointer: !(isSaveDisabled(row) || isSaving),
                                  })}
                                  onClick={() => {
                                    if (!isSaveDisabled(row) && !isSaving && row.id) {
                                      saveItemIndividually(row.id);
                                    }
                                  }}
                                  data-testid={`save-button-${row.slug ?? 'field'}`}
                                >
                                  <Tooltip
                                    render={
                                      isSaving
                                        ? 'Saving...'
                                        : isSaveDisabled(row)
                                          ? hasTitleTooLong
                                            ? `Cannot save: field name can't exceed ${METADATA_TITLE_MAX_LENGTH} characters`
                                            : hasDuplicateTitle
                                              ? 'Cannot save: duplicate title found'
                                              : 'Cannot save: check for duplicate options or empty fields'
                                          : 'Save changes'
                                    }
                                  >
                                    <Icon
                                      icon="checkbox"
                                      width="14"
                                      height="14"
                                      className={isSaveDisabled(row) || isSaving ? 'gray' : 'hover-dark-blue blue'}
                                    />
                                  </Tooltip>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {_notNil(row.field_type) &&
                        selectFields.includes(row.field_type as MetadataFieldTypes) &&
                        getExpandedState(row.id) === true && (
                          <tr>
                            <td></td>
                            <td colSpan={type === 'sample_meta' ? 6 : 5}>
                              <Options
                                key={`${row.id ?? row.index}-${JSON.stringify(row.options)}`}
                                row={row}
                                loading={false}
                                disabled={!row.id || !isEditing(row.id)}
                                onOptionsChange={notifyOptionsChanged}
                              />
                            </td>
                          </tr>
                        )}
                    </Fragment>
                  );
                })}
                {_isNotEmpty(archivedItems) && (
                  <tr className="bg-light-gray hover-bg-near-white">
                    <td className={TABLE_CLASSES.TD}></td>
                    <td
                      className={cn(TABLE_CLASSES.TD, 'pointer')}
                      colSpan={type === 'sample_meta' ? 4 : 3}
                      onClick={() => setShowArchivedItems(!showArchivedItems)}
                      data-testid="toggle-archived-fields"
                    >
                      <span className="blue hover-dark-blue f6">
                        {showArchivedItems ? 'Hide' : 'Show'} archived fields ({archivedItems.length})
                      </span>
                    </td>
                    <td className={TABLE_CLASSES.TD}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt3 mb7">
            <AddField handleAdd={handleAdd} disabled={hasError} type={type} />
          </div>
        </>
      ) : (
        <div className="mv4">
          <Banner info className="mw6" dismiss={false}>
            <h3 className="f5 normal basier-med lh-title">No metadata has been added for {title}</h3>
            <p className="f6 pt2 lh-copy">
              To get started, add metadata relevant to your studies. All team members will have access to the added
              metadata.
            </p>
            <div className="mt3">
              <AddField handleAdd={handleAdd} disabled={hasError} type={type} />
            </div>
          </Banner>
        </div>
      )}
    </div>
  );
};

const AddField: FC<AddCategoryProps> = ({ handleAdd, disabled = false, type }) => {
  // Filter out multi_select for attachment_meta
  const availableFields = Object.entries(selectableFields).filter(([key]) => {
    if (type === 'attachment_meta' && key === 'multi_select') {
      return false;
    }
    return true;
  });

  return (
    <div data-testid="add-field-button">
      <SelectDropDown
        title="Add field"
        className="br-pill"
        btnProps={{
          icon: 'add_new',
          outline: true,
          paleBlue: true,
          disabled,
        }}
        alignMenu="left"
      >
        <ActionList
          actions={availableFields.map(([key, value]) => ({
            name: value,
            action: () => handleAdd(key as MetadataGlossaryFieldType),
          }))}
        />
      </SelectDropDown>
    </div>
  );
};

export default IndividualSaveRepeater;
